import { Card, Divider, Stack, TextField, Typography } from '@mui/material';
import MuiButton from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import useCoupon from '@subflow-frontend/hooks/useCoupon';
import { Product, SubscriberProductResponseDto } from '@subflow/api-client';
import { validateCode } from '@subflow/data-access';
import { useEffect, useState } from 'react';
import { fCurrency } from '../../../../utils/formatNumber';
import { grey } from '@mui/material/colors';

const RootStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0),
  },
  [theme.breakpoints.up(1368)]: {
    padding: theme.spacing(5, 8),
  },
}));

const CardWrapper = styled(Card)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    boxShadow: 'none',
    border: 0,
  },
}));

const ErrorWrapper = styled('div')(({ theme }) => ({
  fontSize: '0.875rem',
  fontFamily: 'Public Sans,sans-serif',
  fontWeight: 400,
  color: 'red',
}));

// ----------------------------------------------------------------------

type Props = {
  product: Product | SubscriberProductResponseDto;
};

export default function SubscriptionOrderSummary({ product }: Props) {
  // @ts-expect-error: interval exists on recurring
  const interval = product?.prices?.[0]?.recurring?.interval;
  const price = `${fCurrency((product?.prices?.[0]?.unitAmount || 0) / 100)}`;
  const [isLoading, setIsLoading] = useState(false);
  const [validCouponError, setValidCouponError] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const { setCouponId } = useCoupon();

  const [discountCode, setDiscountCode] = useState<string>();

  const initProductOrder = async () => {
    const data = await getOrderSummary(product.id);
    setPriceData(data);
  };

  useEffect(() => {
    initProductOrder();
  }, [product]);

  const getDiscountPrice = (coupon, basicPrice) => {
    if (coupon.percent_off) {
      return basicPrice * (coupon.percent_off / 100);
    } else if (coupon.amount_off) {
      return coupon.amount_off;
    }

    return 0;
  };

  const getOrderSummary = async (productId, discountCode?) => {
    const coupon = discountCode
      ? await validateCode(discountCode, product.merchantId)
      : null;

    if (coupon?.data?.length) {
      setCouponId(coupon.data[0].coupon.id);
    }

    const simplePrice = (product?.prices?.[0]?.unitAmount || 0) / 100;

    const discountPrice = coupon?.data?.length
      ? getDiscountPrice(coupon.data[0].coupon, simplePrice)
      : 0;

    return {
      subtotal: price,
      discount: discountPrice ? `${fCurrency(discountPrice)}` : '-',
      hasCoupon: coupon?.data.length > 0 ? true : false,
      hasDiscount: !!discountPrice,
      tax: 0,
      total: `${fCurrency(simplePrice - discountPrice)}`,
    };
  };

  const applyCode = async () => {
    setValidCouponError(false);

    const data = await getOrderSummary(product.id, discountCode);
    if (!data.hasCoupon) {
      setValidCouponError(true);
      setIsLoading(false);
      return;
    }
    setPriceData(data);
    if (data.hasDiscount && data.hasCoupon) {
      setValidCouponError(false);
      setIsLoading(true);
    }
  };

  return (
    <RootStyle>
      <CardWrapper sx={{ backgroundColor: grey[50] }}>
        <Stack spacing={3} mx={{ xs: 0, md: 3 }} pb={3}>
          <div></div>

          <Typography variant="h6">Order Summary</Typography>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="caption">Sub Total</Typography>
            <Typography variant="caption">{priceData?.subtotal}</Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{priceData?.discount}</Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="caption">Tax</Typography>
            <Typography variant="caption">{priceData?.tax || '-'}</Typography>
          </Stack>

          <Divider sx={{ borderStyle: 'solid' }} />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2">Total</Typography>
            <Typography variant="body2" color={'red'}>
              {priceData?.total}
            </Typography>
          </Stack>

          {interval && (
            <Stack position="relative">
              <TextField
                name="discountCode"
                label="Discount codes / Gifts"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
              />
              <MuiButton
                onClick={async (e) => {
                  e.preventDefault();
                  setIsLoading(true);
                  await applyCode();
                }}
                disabled={isLoading}
                variant="text"
                type="submit"
                sx={{
                  my: 'auto',
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                }}
              >
                Apply
              </MuiButton>
            </Stack>
          )}
          <ErrorWrapper>
            {validCouponError && 'Your coupon is not valid'}
          </ErrorWrapper>
        </Stack>
      </CardWrapper>
    </RootStyle>
  );
}
